import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export interface PlayPauseControlsProps {
    /** Whether the controlled media is currently playing */
    isPlaying: boolean;
    /** Called when the user clicks the button to toggle play/pause */
    onToggle: () => void;
    /** Accessible label for the controlled media (e.g. "Product demo animation").
     *  Used to build descriptive aria-labels: "Play Product demo animation" / "Pause Product demo animation" */
    mediaLabel?: string;
}

export const PlayPauseControls = ({
    isPlaying,
    onToggle,
    mediaLabel
}: PlayPauseControlsProps) => {
    const intl = useIntl();

    const playLabel = mediaLabel
        ? intl.formatMessage(
              {
                  id: "play-pause-controls.play-with-label",
                  defaultMessage: "Play {mediaLabel}"
              },
              { mediaLabel }
          )
        : intl.formatMessage({
              id: "play-pause-controls.play",
              defaultMessage: "Play"
          });

    const pauseLabel = mediaLabel
        ? intl.formatMessage(
              {
                  id: "play-pause-controls.pause-with-label",
                  defaultMessage: "Pause {mediaLabel}"
              },
              { mediaLabel }
          )
        : intl.formatMessage({
              id: "play-pause-controls.pause",
              defaultMessage: "Pause"
          });

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={isPlaying ? pauseLabel : playLabel}
            aria-pressed={isPlaying}
            data-testid="play-pause-controls"
            css={controlButtonCss}
        >
            <div css={buttonCircleCss}>
                <span css={iconGlyphCss}>
                    {isPlaying ? (
                        <VideoPauseOverlayIcon
                            label=""
                            size="medium"
                            color="currentColor"
                        />
                    ) : (
                        <VideoPlayOverlayIcon
                            label=""
                            size="medium"
                            color="currentColor"
                        />
                    )}
                </span>
            </div>
        </button>
    );
};

const controlButtonCss = css({
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "center",
    "padding": token("space.0"),
    "backgroundColor": "transparent",
    "border": "none",
    "borderRadius": "100%",
    "cursor": "pointer",
    "height": "40px",
    "width": "40px",
    // Focus ring for keyboard accessibility
    "&:focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

const buttonCircleCss = css({
    "backgroundColor": wacToken("color.neutral.light300"),
    "borderRadius": "100%",
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    "flexShrink": 0,
    "height": "40px",
    "width": "40px",
    // Tablet & below: shrink the visible circle to 32px while keeping the
    // 40x40 button as the tap target (per GWP-11496).
    [media.below.lg]: {
        height: "32px",
        width: "32px"
    },
    "&:hover": {
        backgroundColor: wacToken("color.neutral.light400")
    },
    "&:active": {
        backgroundColor: wacToken("color.neutral.light400")
    }
});

/** Figma: black glyphs on the neutral circular background */
const iconGlyphCss = css({
    color: wacToken("color.primary.black"),
    display: "flex",
    lineHeight: 0
});
